import { IDENTITY } from '../geometry/matrix';
import { getShapePreset, NAIL_SIZE_PRESETS } from '../nails/shapes';
import type { IdGenerator } from './ids';
import {
  CURRENT_SCHEMA_VERSION,
  type Design,
  type FingerId,
  type Hand,
  type NailInstance,
  type NailShapeId,
  type ProjectDocument,
  type StrokeStyle,
} from './types';

/** 0.15 mm fixed-width round-cap line work is the proposed starting default (docs/04). */
export const DEFAULT_STROKE: StrokeStyle = {
  color: '#2E2530',
  widthMm: 0.15,
  opacity: 1,
  lineCap: 'round',
  lineJoin: 'round',
  scaleMode: 'fixed-mm',
};

/** Practical stroke range; values below the print-quality threshold get a warning, not a block. */
export const STROKE_LIMITS = { minMm: 0.05, maxMm: 1.2, printWarningBelowMm: 0.1 } as const;

/** A single 14 × 22 mm example artboard (docs/01) — a starting example, not a nail measurement. */
export const DEFAULT_ARTBOARD = {
  widthMm: NAIL_SIZE_PRESETS.medium.widthMm,
  heightMm: NAIL_SIZE_PRESETS.medium.lengthMm,
} as const;

export const DEFAULT_NAIL_SHAPE: NailShapeId = 'oval';
export const DEFAULT_BASE_COLOR = '#F6E3E6';

export function createDesign(ids: IdGenerator, name = 'Design 1'): Design {
  return { id: ids('design'), name, artboard: { ...DEFAULT_ARTBOARD }, nodes: [] };
}

export function createNail(
  ids: IdGenerator,
  designId: string,
  options: {
    hand?: Hand;
    finger?: FingerId;
    shape?: NailShapeId;
    widthMm?: number;
    lengthMm?: number;
  } = {},
): NailInstance {
  return {
    id: ids('nail'),
    hand: options.hand ?? 'right',
    finger: options.finger ?? 'index',
    designId,
    shape: getShapePreset(options.shape ?? DEFAULT_NAIL_SHAPE),
    widthMm: options.widthMm ?? DEFAULT_ARTBOARD.widthMm,
    lengthMm: options.lengthMm ?? DEFAULT_ARTBOARD.heightMm,
    placement: IDENTITY,
    baseColor: DEFAULT_BASE_COLOR,
    finish: 'gloss',
  };
}

/** A new single-nail project: one editable design shown on one nail. */
export function createProject(options: {
  ids: IdGenerator;
  now: Date;
  name?: string;
  shape?: NailShapeId;
}): ProjectDocument {
  const design = createDesign(options.ids);
  const nail = createNail(options.ids, design.id, options.shape ? { shape: options.shape } : {});
  const timestamp = options.now.toISOString();
  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    id: options.ids('project'),
    name: options.name ?? 'Untitled design',
    createdAt: timestamp,
    updatedAt: timestamp,
    revision: 0,
    designs: [design],
    nails: [nail],
  };
}
